/**
 * @file attachment-list.component.tsx
 * @description Универсальный компонент для отображения и управления списком прикреплений различных типов:
 * файлы, внутренние объекты системы и внешние ссылки. Поддерживает сортировку по типу,
 * динамическое рендеринг компонентов-обработчиков и операции редактирования/удаления с коллбэками изменений.
 * @author Genotroid
 * @created 2025-09-03
 * @updated 2025-09-18
 * @dependencies [react, @consta/uikit/Text, @src/types/TAttachment, @utils/data-helper]
 * @related-files [./components/file.component.tsx, ./components/object.component.tsx, ./components/link.component.tsx]
 */

import React, { createElement, RefObject, useEffect, useRef } from 'react';
import { Text } from '@consta/uikit/Text';
import { File } from './components/file.component';
import { Object } from './components/object.component';
import { Link } from './components/link.component';
import { TAttachment } from '@src/types';
import { ErrorListHandle } from '@components/ui/indicators/error-list';
import { isFileAttachment, isLinkAttachment, isSystemObjectAttachment } from '@utils/data-helper';

/**
 * Props для компонента списка вложений
 */
interface IAttachmentListProps {
    /** Список вложений для отображения */
    attachments: TAttachment[];
    /** Обработчик изменения списка вложений */
    onChange: (attachments: TAttachment[]) => void;
}

/**
 * Props для компонентов отображения отдельного вложения
 */
export interface IAttachmentComponentProps {
    /** Данные вложения */
    attachment: TAttachment;
    /** Обработчик изменения вложения */
    onChange: (newAttachment: TAttachment, type: 'edit' | 'delete', oldValue?: TAttachment) => void;
    errorListRef?: RefObject<ErrorListHandle>;
}

export const AttachmentList = ({ attachments, onChange }: IAttachmentListProps) => {
    const attachmentsRef = useRef(attachments);

    useEffect(() => {
        attachmentsRef.current = attachments;
    }, [attachments]);

    const onChangeHandler = (newAttachment: TAttachment, type: 'edit' | 'delete', oldValue?: TAttachment) => {
        switch (type) {
            case 'edit':
                if (!oldValue) return;
                
                const newAttachmentList = attachmentsRef.current.map((attach: TAttachment) => {
                    if (attach.link === oldValue.link) {
                        return { ...attach, ...newAttachment };
                    }

                    return attach;
                });
                onChange(newAttachmentList);
                break;
            case 'delete':
                onChange(attachmentsRef.current.filter((item: TAttachment) => item.link !== newAttachment.link));
                break;
        }
    };

    return (
        <div className="attachment-list flex flex-wrap gap-[0.8468vmin]">
            {Boolean(attachments?.length) ? (
                attachments.map((attachment: TAttachment, key: number) => (
                    <div
                        className="attachment-list__file flex gap-[0.6351vmin] items-center hidden-buttons__container"
                        key={key}
                    >
                        {(() => {
                            const Component = getComponentByAttachType(attachment);
                            return Component
                                ? createElement(Component, {
                                      onChange: onChangeHandler,
                                      attachment
                                  })
                                : null;
                        })()}
                    </div>
                ))
            ) : (
                <Text size="s">Нет данных</Text>
            )}
        </div>
    );
};

const getComponentByAttachType = (attachment: TAttachment): React.ComponentType<IAttachmentComponentProps> | null => {
    if (isFileAttachment(attachment)) {
        return File;
    }

    if (isLinkAttachment(attachment)) {
        return Link;
    }

    if (isSystemObjectAttachment(attachment)) {
        return Object;
    }

    return null;

    // // Fallback для старой логики по ID типа
    // switch (attachment.type?.id) {
    //     case '1':
    //         return File;
    //     case '2':
    //         return Link;
    //     case '3':
    //         return Object;
    //     default:
    //         return null;
    // }
};
